/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { useCallback, useState } from 'react';
import { useVideoContext } from '../videoContext';

/**
 * Used solely to toggle the local video track on and off. For Mobile users when they background the browser app.
 * TODO: (ZOOMSDK) - not fully supported. add support for this hook in Zoom
 * @see client/src/components/twilio/AttachVisibilityHandler/AttachVisibilityHandler.tsx
 */
export default function useLocalVideoToggle(): readonly [boolean, () => void] {
  const { room, localTracks, getLocalVideoTrack, removeLocalVideoTrack } = useVideoContext();
  const localParticipant = room?.localParticipant;
  const videoTrack = localTracks.find(track => !track.name.includes('screen') && track.kind === 'video');
  const [isPublishing, setIsPublishing] = useState(false);

  const toggleVideoEnabled = useCallback(() => {
    if (!isPublishing) {
      if (videoTrack) {
        const localTrackPublication = localParticipant?.unpublishTrack?.(videoTrack);
        localParticipant?.emit?.('trackUnpublished', localTrackPublication);
        removeLocalVideoTrack();
      } else {
        setIsPublishing(true);
        getLocalVideoTrack()
          .then((track: VideoProvider.ILocalVideoTrack) => localParticipant?.publishTrack?.(track, { priority: 'low' }))
          // eslint-disable-next-line no-console
          .catch(console.error)
          .finally(() => {
            setIsPublishing(false);
          });
      }
    }
  }, [videoTrack, localParticipant, getLocalVideoTrack, isPublishing, removeLocalVideoTrack]);

  return [!!videoTrack, toggleVideoEnabled];
}
